import type { TableView, TableViewPrefs, ViewContent, ViewVisibility } from './types';
import { normalizeColumnWidth } from './columnOps';

/** Stored widths by view and column, keeping only real widths — storage (or the backend) can hold anything. */
export const cleanColumnWidths = (raw: unknown): Record<string, Record<string, number>> =>
  Object.fromEntries(
    Object.entries(raw && typeof raw === 'object' ? raw : {})
      .map(([viewId, widths]) => [
        viewId,
        Object.fromEntries(
          Object.entries(widths && typeof widths === 'object' ? widths : {})
            .map(([columnId, width]) => [columnId, normalizeColumnWidth(width)])
            .filter(([, width]) => width !== undefined),
        ),
      ])
      .filter(([, widths]) => Object.keys(widths).length > 0),
  );

/**
 * Where views and the user's tab preferences live: the API ({@link createApiViewStore}) or, until the
 * backend is deployed, localStorage ({@link createLocalViewStore}). The table never learns which.
 *
 * The store owns ids and versions: callers continue with the view a call returns, never with the one
 * they passed in. A failed call rejects with a {@link TableViewStoreError} once the store has told the
 * user why, so the caller only has to leave its state as it was.
 */
export interface TableViewStore {
  /** The user's views (own and shared with them) and their tab preferences, in one go. */
  load: (tableKey: string) => Promise<{ views: TableView[]; prefs: TableViewPrefs }>;
  /** A new private view of the user's. The returned one carries the real id and version. `silent`: no success toast. */
  createView: (view: TableView, options?: { silent?: boolean }) => Promise<TableView>;
  /** Saves the name and content. The view's `version` must be the one last read; a stale one rejects as `conflict`. */
  updateView: (view: TableView) => Promise<TableView>;
  /** Shares the view with the user's company (`COMPANY`) or takes it back (`PRIVATE`). Versioned like `updateView`. */
  setVisibility: (view: TableView, visibility: ViewVisibility) => Promise<TableView>;
  deleteView: (tableKey: string, viewId: string) => Promise<void>;
  savePrefs: (prefs: TableViewPrefs) => Promise<TableViewPrefs>;
}

/**
 * Why a store call failed. `conflict`: the view was saved elsewhere in the meantime (another window,
 * another device) — reload it. `failed`: anything else; the store has already shown the reason.
 */
export class TableViewStoreError extends Error {
  constructor(readonly kind: 'conflict' | 'failed') {
    super(`Table view store: ${kind}`);
  }
}

/**
 * Unsaved working copies, per tab. Browser-only on purpose: an edit in progress belongs to this
 * device, not to the account, so this stays local even once views move to the backend.
 */
export interface ViewDraftStore {
  getDrafts: (tableKey: string) => Record<string, ViewContent>;
  setDraft: (tableKey: string, viewId: string, draft: ViewContent | undefined) => void;
}

/** The subset of `Storage` the stores use — injectable, so tests don't need a browser. */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const read = <T>(storage: KeyValueStorage, key: string, fallback: T): T => {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // Unparseable entries are treated as absent; the next write replaces them.
    return fallback;
  }
};

const write = (storage: KeyValueStorage, key: string, value: unknown) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or privacy mode: the change still applies for this session, it just won't survive a reload.
  }
};

const viewsKey = (userId: string, tableKey: string) => `user_${userId}_table_views_${tableKey}`;
const prefsKey = (userId: string, tableKey: string) => `user_${userId}_table_view_prefs_${tableKey}`;
const draftsKey = (userId: string, tableKey: string) => `user_${userId}_table_view_drafts_${tableKey}`;

/**
 * Views and prefs in localStorage, per user and table. It keeps the ids it is given and bumps the
 * version on every update, as the backend does. Values go out as copies, so callers can't mutate the store.
 */
export const createLocalViewStore = (userId: string, storage: KeyValueStorage = window.localStorage): TableViewStore => {
  const listViews = (tableKey: string) => read<TableView[]>(storage, viewsKey(userId, tableKey), []).filter(view => view && typeof view.id === 'string');
  const put = (view: TableView) => {
    const views = listViews(view.tableKey);
    const index = views.findIndex(existing => existing.id === view.id);
    const next = index === -1 ? [...views, view] : views.map(existing => (existing.id === view.id ? view : existing));
    write(storage, viewsKey(userId, view.tableKey), next);
    return structuredClone(view);
  };
  return {
    load: async tableKey => {
      const stored = read<Partial<TableViewPrefs>>(storage, prefsKey(userId, tableKey), {});
      return {
        views: listViews(tableKey),
        prefs: {
          tableKey,
          order: Array.isArray(stored.order) ? stored.order.filter(id => typeof id === 'string') : [],
          hidden: Array.isArray(stored.hidden) ? stored.hidden.filter(id => typeof id === 'string') : [],
          ...(typeof stored.activeViewId === 'string' ? { activeViewId: stored.activeViewId } : {}),
          ...(stored.pagingModes && typeof stored.pagingModes === 'object'
            ? { pagingModes: Object.fromEntries(Object.entries(stored.pagingModes).filter(([, mode]) => mode === 'pages' || mode === 'scroll')) }
            : {}),
          ...(stored.columnWidths && typeof stored.columnWidths === 'object' ? { columnWidths: cleanColumnWidths(stored.columnWidths) } : {}),
        },
      };
    },
    createView: async view => put(view),
    updateView: async view => put({ ...view, version: view.version + 1 }),
    // Nobody else reads this browser's storage, so a shared view here is only marked as one.
    setVisibility: async (view, visibility) => put({ ...view, visibility, version: view.version + 1 }),
    deleteView: async (tableKey, viewId) => {
      write(
        storage,
        viewsKey(userId, tableKey),
        listViews(tableKey).filter(view => view.id !== viewId),
      );
    },
    savePrefs: async prefs => {
      write(storage, prefsKey(userId, prefs.tableKey), prefs);
      return structuredClone(prefs);
    },
  };
};

export const createLocalDraftStore = (userId: string, storage: KeyValueStorage = window.localStorage): ViewDraftStore => ({
  getDrafts: tableKey => read<Record<string, ViewContent>>(storage, draftsKey(userId, tableKey), {}),
  setDraft: (tableKey, viewId, draft) => {
    const drafts = read<Record<string, ViewContent>>(storage, draftsKey(userId, tableKey), {});
    if (draft) drafts[viewId] = draft;
    else delete drafts[viewId];
    if (Object.keys(drafts).length === 0) storage.removeItem(draftsKey(userId, tableKey));
    else write(storage, draftsKey(userId, tableKey), drafts);
  },
});
